import React from "react";

import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const COLORS = ["#14b8a6", "#3b82f6", "#f59e0b", "#ef4444"];

const isNumericValue = (value) => {
  if (typeof value === "number") return true;
  if (typeof value !== "string") return false;

  const cleaned = value.replace(/[£,\s]/g, "");
  return cleaned !== "" && !Number.isNaN(Number(cleaned));
};

const renderCellContent = (cell) => {
  if (cell && typeof cell === "object" && cell.type === "customer") {
    return (
      <div className="flex items-center">
        <span>{cell.label}</span>
        {cell.isInternal ? (
          <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            Admin
          </span>
        ) : null}
      </div>
    );
  }

  return cell;
};

const StatCard = ({
  title,
  value,
  icon,
  accent = "bg-emerald-500",
  valueClass = "text-slate-900",
}) => {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className={`h-1 w-full ${accent}`} />
      <div className="p-5">
        <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-2xl">
          {icon}
        </div>
        <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
          {title}
        </p>
        <p className={`mt-2 text-2xl font-bold tracking-tight ${valueClass}`}>
          {value}
        </p>
      </div>
    </div>
  );
};

const SectionCard = ({ title, children, action }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <h2 className="text-base font-semibold text-slate-800">{title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
};

const DataTable = ({ headers, rows, emptyText = "No data available" }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
            {headers.map((header, index) => (
              <th
                key={header}
                className={`py-3 font-bold align-top break-words ${
                  index === 0 ? "w-[44%] pr-3" : "w-[28%] px-2 text-center"
                }`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.length ? (
            rows.map((row, idx) => (
              <tr
                key={idx}
                onClick={row.onClick}
                className={`border-b border-slate-50 last:border-b-0 ${
                  row.isInternal ? "bg-amber-50/80" : ""
                } ${row.onClick ? "cursor-pointer transition hover:bg-sky-50" : ""}`}
              >
                {row.cells.map((cell, cellIdx) => {
                  const renderedCell = renderCellContent(cell);
                  const isNumber =
                    typeof cell === "object" && cell?.type === "customer"
                      ? false
                      : isNumericValue(String(cell));

                  return (
                    <td
                      key={cellIdx}
                      className={`py-3 align-middle ${
                        cellIdx === 0
                          ? row.isInternal
                            ? "pr-3 text-amber-800"
                            : "pr-3 text-slate-700"
                          : row.isInternal
                            ? "px-2 text-center tabular-nums text-amber-800"
                            : "px-2 text-center tabular-nums text-slate-700"
                      } ${isNumber ? "font-medium" : ""}`}
                    >
                      {renderedCell}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={headers.length}
                className="py-8 text-center text-sm italic text-slate-400"
              >
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const AnalyticsSection = ({
  type,
  statCards = [],
  sectionConfigs = [],
  onViewAll,
  ordersByType = [],
  totalOrderTypeRevenue = 0,
  paymentMethods = [],
  totalPaymentMethodRevenue = 0,
}) => {
  if (type === "stats") {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {statCards.map((card) => (
          <StatCard key={card.title} {...card} />
        ))}
      </div>
    );
  }

  if (type === "tables") {
    return (
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {sectionConfigs.map((section) => (
          <SectionCard
            key={section.title}
            title={section.title}
            action={
              section.data.length > 5 ? (
                <button
                  className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                  onClick={() => onViewAll(section.data, section.modalTitle)}
                >
                  View All
                </button>
              ) : null
            }
          >
            <DataTable headers={section.headers} rows={section.previewRows} />
          </SectionCard>
        ))}
      </div>
    );
  }

  if (type === "ordersByType") {
    return (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        {/* LEFT: Pie */}
        <SectionCard title="Orders by Type">
          {ordersByType.length > 0 ? (
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    dataKey="count"
                    data={ordersByType}
                    nameKey="type"
                    outerRadius={120}
                    innerRadius={60}
                    paddingAngle={3}
                    labelLine={false}
                    label={({ type, count }) => `${type}: ${count}`}
                  >
                    {ordersByType.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="py-12 text-center text-sm text-slate-400">
              No data available
            </div>
          )}
        </SectionCard>

        {/* RIGHT: Revenue */}
        <SectionCard title="Order Type Revenue">
          <div className="space-y-4">
            {ordersByType.map((entry, index) => {
              const percent = totalOrderTypeRevenue
                ? (
                    (Number(entry.revenue || 0) / totalOrderTypeRevenue) *
                    100
                  ).toFixed(1)
                : 0;

              return (
                <div
                  key={entry.type}
                  className="rounded-xl border border-slate-100 bg-slate-50 p-4"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-block h-3 w-3 rounded-full"
                        style={{
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                      <span className="font-medium text-slate-700">
                        {entry.type}
                      </span>
                    </div>

                    <span className="text-sm font-semibold text-slate-900">
                      £{Number(entry.revenue || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="h-2 rounded-full bg-slate-200">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${percent}%`,
                        backgroundColor: COLORS[index % COLORS.length],
                      }}
                    />
                  </div>

                  <div className="mt-1 text-xs text-slate-500">
                    {percent}% of revenue
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>
    );
  }

  if (type === "paymentMethods") {
    const labelMap = {
      cash: "Pay in Store",
      online: "Online",
      unknown: "Unknown",
    };

    return (
      <SectionCard title="Payment Method Revenue">
        <div className="space-y-4">
          {paymentMethods.length > 0 ? (
            paymentMethods.map((entry, index) => {
              const percent = totalPaymentMethodRevenue
                ? (
                    (Number(entry.total_revenue || 0) /
                      totalPaymentMethodRevenue) *
                    100
                  ).toFixed(1)
                : 0;

              return (
                <div
                  key={entry.method}
                  className="rounded-xl border border-slate-100 bg-slate-50 p-4"
                >
                  {/* Top row */}
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-block h-3 w-3 rounded-full"
                        style={{
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                      <span className="font-medium text-slate-700">
                        {labelMap[entry.method] || entry.method}
                      </span>
                    </div>

                    <span className="text-sm font-semibold text-slate-900">
                      £{Number(entry.total_revenue || 0).toFixed(2)}
                    </span>
                  </div>

                  {/* Orders count */}
                  <div className="mb-2 text-xs text-slate-500">
                    {entry.total_orders} orders
                  </div>

                  {/* Progress bar */}
                  <div className="h-2 rounded-full bg-slate-200">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${percent}%`,
                        backgroundColor: COLORS[index % COLORS.length],
                      }}
                    />
                  </div>

                  {/* Percentage */}
                  <div className="mt-1 text-xs text-slate-500">
                    {percent}% of revenue
                  </div>
                </div>
              );
            })
          ) : (
            <div className="py-12 text-center text-sm text-slate-400">
              No data available
            </div>
          )}
        </div>
      </SectionCard>
    );
  }

  return null;
};

export default AnalyticsSection;
